import { useTranslation } from 'react-i18next';
import { Typography, Card, CardContent, Box, Chip, Stack } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/TopSeriesSummaryFilterSection';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import { showLabel } from '@/utils/helper';
import { useGetTopSeriesListQuery } from '@/api/exercise/topSeriesApiSlice';
import {
	TopSeriesFilter,
	TopSeriesListDTO,
} from '@/api/DTO/exercise/topSeries.interface';
import { OnoffType } from '@/api/enum/common.enum';
import SetOrderDialog from './components/SetOrderDialog';
import AddExerciseDialog from './components/AddExerciseDialog';

export interface ListDataGrid {
	updatedAt: string;
}

export default function TopSeriesListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const statusList = [
		{ id: OnoffType.off, name: t('meditation.off') },
		{ id: OnoffType.on, name: t('meditation.on') },
	];

	const exerciseTypeList = [
		{
			id: 1,
			name: t('moment.meditation'),
		},
		{
			id: 2,
			name: t('moment.body'),
		},
		{
			id: 3,
			name: t('moment.crossover'),
		},
	];

	const defaultPropertyFilter: TopSeriesFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<TopSeriesFilter> = defaultPropertyFilter;
	const methods = useForm<TopSeriesFilter>({
		defaultValues,
	});
	const transformedSearchParams: TopSeriesFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetTopSeriesListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: TopSeriesListDTO[]) => {
		if (response == null) return;
		const newResponse: ListDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as TopSeriesFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('exerciseSetting.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseType',
			headerName: t('exerciseSetting.seriesType'),
			flex: 15,
			renderCell: (params: any) =>
				showLabel(params.row.exerciseType, exerciseTypeList),
		},
		{
			field: 'singleCount',
			headerName: t('exerciseSetting.exerciseCount'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('exerciseSetting.seriesStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('exerciseSetting.lastUpdateTime'),
			flex: 21,
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.topSeries')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<AddExerciseDialog />
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
